// Existing formatters from the original file
export const formatCurrency = (value, currency = 'USD', options = {}) => {
  if (value === undefined || value === null) return '-';
  
  const defaults = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  
  const mergedOptions = { ...defaults, ...options };
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      ...mergedOptions,
    }).format(value);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return value?.toString() || '-';
  }
};

export const formatPercent = (value, options = {}) => {
  if (value === undefined || value === null) return '-';
  
  const defaults = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: 'always',
  };
  
  const mergedOptions = { ...defaults, ...options };
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      ...mergedOptions,
    }).format(value / 100);
  } catch (error) {
    console.error('Error formatting percentage:', error);
    return value?.toString() + '%' || '-';
  }
};

export const formatNumber = (value, options = {}) => {
  if (value === undefined || value === null) return '-';
  
  const defaults = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  
  const mergedOptions = { ...defaults, ...options };
  
  try {
    return new Intl.NumberFormat('en-US', mergedOptions).format(value);
  } catch (error) {
    console.error('Error formatting number:', error);
    return value?.toString() || '-';
  }
};

export const formatDate = (date, options = {}) => {
  if (!date) return '-';
  
  try {
    const dateObj = new Date(date);
    
    const defaults = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    
    const mergedOptions = { ...defaults, ...options };
    
    return new Intl.DateTimeFormat('en-US', mergedOptions).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error);
    return date?.toString() || '-';
  }
};

export const formatTime = (date, options = {}) => {
  if (!date) return '-';
  
  try {
    const dateObj = new Date(date);
    
    const defaults = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    
    const mergedOptions = { ...defaults, ...options };
    
    return new Intl.DateTimeFormat('en-US', mergedOptions).format(dateObj);
  } catch (error) {
    console.error('Error formatting time:', error);
    return date?.toString() || '-';
  }
};

export const formatDateTime = (date, options = {}) => {
  if (!date) return '-';
  
  try {
    let dateStr = date.toString();
    if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('GMT')) {
      dateStr = dateStr.replace(' ', 'T') + 'Z';
    }
    
    const dateObj = new Date(dateStr);
    
    const defaults = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    
    const mergedOptions = { ...defaults, ...options };
    
    return new Intl.DateTimeFormat('en-US', mergedOptions).format(dateObj);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return date?.toString() || '-';
  }
};

export const getValueColor = (value) => {
  if (value === undefined || value === null) return 'gray.500';
  return parseFloat(value) >= 0 ? 'green.500' : 'red.500';
};

export const getTextColor = (colorMode) => {
  return colorMode === 'dark' ? 'whiteAlpha.900' : 'gray.800';
};

export const getBgColor = (colorMode) => {
  return colorMode === 'dark' ? 'gray.800' : 'white';
};

export const timeAgo = (date) => {
  if (!date) return '-';
  
  try {
    const now = new Date();
    const utcNow = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds()
    );
    
    let dateStr = date.toString();
    if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('GMT')) {
      dateStr = dateStr.replace(' ', 'T') + 'Z';
    }
    
    const utcDate = new Date(dateStr).getTime();
    const seconds = Math.floor((utcNow - utcDate) / 1000);
    const absSeconds = Math.abs(seconds);
    
    let interval = absSeconds / 31536000;
    if (interval > 1) {
      return Math.floor(interval) + (seconds < 0 ? ' years from now' : ' years ago');
    }
    
    interval = absSeconds / 2592000;
    if (interval > 1) {
      return Math.floor(interval) + (seconds < 0 ? ' months from now' : ' months ago');
    }
    
    interval = absSeconds / 86400;
    if (interval > 1) {
      return Math.floor(interval) + (seconds < 0 ? ' days from now' : ' days ago');
    }
    
    interval = absSeconds / 3600;
    if (interval > 1) {
      return Math.floor(interval) + (seconds < 0 ? ' hours from now' : ' hours ago');
    }
    
    interval = absSeconds / 60;
    if (interval > 1) {
      return Math.floor(interval) + (seconds < 0 ? ' minutes from now' : ' minutes ago');
    }
    
    if (absSeconds < 10) {
      return 'just now';
    }
    
    return Math.floor(absSeconds) + (seconds < 0 ? ' seconds from now' : ' seconds ago');
  } catch (error) {
    console.error('Error calculating time ago:', error);
    return date?.toString() || '-';
  }
};

// NEW: Bitget-style price formatting for crypto trading
export const formatBitgetPrice = (price, symbol = '') => {
  if (!price || price === 0) return '0';
  
  const priceNum = parseFloat(price);
  if (isNaN(priceNum) || priceNum === 0) return '0';
  
  let decimals;
  
  // For small values (less than 1)
  if (priceNum < 1) {
    // Find first non-zero digit after decimal
    const priceStr = priceNum.toString();
    const decimalIndex = priceStr.indexOf('.');
    let firstNonZero = 0;
    
    if (decimalIndex !== -1) {
      for (let i = decimalIndex + 1; i < priceStr.length; i++) {
        if (priceStr[i] !== '0') {
          firstNonZero = i - decimalIndex - 1;
          break;
        }
      }
    }
    
    // Show 4-5 digits after first non-zero digit
    decimals = Math.min(firstNonZero + 5, 8);
  }
  // Between 1 and 100
  else if (priceNum < 100) {
    decimals = 4;
  }
  // Between 100 and 1000
  else if (priceNum < 1000) {
    decimals = 3;
  }
  // 1000 and above
  else {
    decimals = 2;
  }
  
  // Format price and remove trailing zeros
  let formattedPrice = priceNum.toFixed(decimals);
  
  // Remove trailing zeros after decimal point
  if (formattedPrice.includes('.')) {
    formattedPrice = formattedPrice.replace(/\.?0+$/, '');
  }
  
  return formattedPrice;
};

// Enhanced percentage formatting for trading
export const formatPercentage = (number, decimals = 2) => {
  if (isNaN(number) || number === null || number === undefined) return '0.00';
  return parseFloat(number).toFixed(decimals);
};

// Format size/volume with proper notation
export const formatTradeSize = (size, symbol = '') => {
  if (!size || size === 0) return '0';
  
  const sizeNum = parseFloat(size);
  if (isNaN(sizeNum)) return '0';
  
  // For very large numbers, use K/M notation
  if (sizeNum >= 1000000) {
    return (sizeNum / 1000000).toFixed(2) + 'M';
  } else if (sizeNum >= 1000) {
    return (sizeNum / 1000).toFixed(2) + 'K';
  } else if (sizeNum >= 1) {
    return sizeNum.toFixed(2);
  } else {
    return sizeNum.toFixed(6);
  }
};

// Format margin/collateral values
export const formatMargin = (margin) => {
  if (!margin || margin === 0) return '0';
  
  const marginNum = parseFloat(margin);
  if (isNaN(marginNum)) return '0';
  
  return marginNum.toFixed(2);
};

// Calculate and format risk-reward ratio
export const formatRiskReward = (entryPrice, takeProfit, stopLoss, direction) => {
  const entry = parseFloat(entryPrice) || 0;
  const tp = parseFloat(takeProfit) || 0;
  const sl = parseFloat(stopLoss) || 0;
  
  if (entry === 0 || tp === 0 || sl === 0) return '-';
  
  let reward, risk;
  
  if (direction === 'long') {
    reward = Math.abs(tp - entry);
    risk = Math.abs(entry - sl);
  } else {
    reward = Math.abs(entry - tp);
    risk = Math.abs(sl - entry);
  }
  
  if (risk === 0) return 'Undefined';
  
  const ratio = (reward / risk).toFixed(2);
  return `${ratio}:1`;
};

// Format PnL with proper color coding
export const formatPnLWithColor = (pnl, isPercentage = false) => {
  if (pnl === undefined || pnl === null || isNaN(pnl)) {
    return { text: '-', color: 'gray.500' };
  }
  
  const numValue = parseFloat(pnl);
  const isPositive = numValue >= 0;
  const color = isPositive ? 'green.400' : 'red.400';
  
  let text;
  if (isPercentage) {
    text = `${isPositive ? '+' : ''}${formatPercentage(numValue)}%`;
  } else {
    text = `${isPositive ? '+' : ''}$${formatPercentage(numValue)}`;
  }
  
  return { text, color };
};

// Format leverage display
export const formatLeverage = (leverage) => {
  if (!leverage || leverage === 0) return '1x';
  
  const leverageNum = parseFloat(leverage);
  if (isNaN(leverageNum)) return '1x';
  
  return `${Math.round(leverageNum)}x`;
};

// Distance calculation for TP/SL
export const calculateDistance = (fromPrice, toPrice, isPercentage = true) => {
  const from = parseFloat(fromPrice) || 0;
  const to = parseFloat(toPrice) || 0;
  
  if (from === 0 || to === 0) return '-';
  
  if (isPercentage) {
    const percentage = Math.abs(((to - from) / from) * 100);
    return `${percentage.toFixed(2)}%`;
  } else {
    const difference = Math.abs(to - from);
    return formatBitgetPrice(difference);
  }
};

// Format liquidation price
export const formatLiquidationPrice = (liquidationPrice, symbol = '') => {
  if (!liquidationPrice || liquidationPrice === 0) return '-';
  
  return `$${formatBitgetPrice(liquidationPrice, symbol)}`;
};

// Format unrealized PnL in dollars
export const formatUnrealizedPnL = (position) => {
  const { size, livePrice, entryPrice, direction, leverage = 1 } = position;
  
  if (!size || !livePrice || !entryPrice) return { text: '-', color: 'gray.500' };
  
  const sizeNum = parseFloat(size);
  const liveNum = parseFloat(livePrice);
  const entryNum = parseFloat(entryPrice);
  const leverageNum = parseFloat(leverage);
  
  if (isNaN(sizeNum) || isNaN(liveNum) || isNaN(entryNum)) {
    return { text: '-', color: 'gray.500' };
  }
  
  let pnlUsd;
  if (direction === 'long') {
    pnlUsd = (liveNum - entryNum) * sizeNum * leverageNum;
  } else {
    pnlUsd = (entryNum - liveNum) * sizeNum * leverageNum;
  }
  
  const isPositive = pnlUsd >= 0;
  const color = isPositive ? 'green.400' : 'red.400';
  const text = `${isPositive ? '+' : ''}$${Math.abs(pnlUsd).toFixed(2)}`;
  
  return { text, color };
};

export default {
  formatCurrency,
  formatPercent,
  formatNumber,
  formatDate,
  formatTime,
  formatDateTime,
  getValueColor,
  getTextColor,
  getBgColor,
  timeAgo,
  formatBitgetPrice,
  formatPercentage,
  formatTradeSize,
  formatMargin,
  formatRiskReward,
  formatPnLWithColor,
  formatLeverage,
  calculateDistance,
  formatLiquidationPrice,
  formatUnrealizedPnL
};